// src/pages/companies/tabs/CredentialsTab.jsx — SOUND-709
//
// The company-scoped credential list. Same house components as MidsTab and
// TicketsTab, scoped to one company.
//
// WHY THIS TAB IS THE HONEST PLACE FOR IT. Ownership is a companyId ON the
// credential — that is the permission anchor, and it is exactly one company.
// Consumption is a link, and it is many. So "this company's credentials" is a
// real, answerable question, whereas "this MID's credentials" is a view of the
// consumption side and belongs on the MID record.
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCredentials } from "hooks/useCredentials";
import { CREDENTIAL_KIND, statusToneFrom } from "utilities/statusTones";
import { hasSecretStorage } from "utilities/credentialFields";
import RecordTable from "components/Records/RecordTable";
import { parseSort } from "utilities/listSort";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import RelativeTime from "components/RelativeTime";
import { KeyRound, Plus } from "lucide-react";
import CredentialCreateModal from "pages/integrations/CredentialCreateModal";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { isAggregatedCredentialView, credentialOwnerLabel } from "utilities/credentialOwners";

const kindTone = statusToneFrom(CREDENTIAL_KIND);

export default function CompanyCredentialsTab({ companyId }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");
  const [showCreate, setShowCreate] = useState(false);
  const { user } = useAuth();
  const isAdmin = isInternal(user);

  const { credentials, summary, pagination, loading, error, refetch } = useCredentials({
    page, limit, q, sortBy: parseSort(sort).field, sortDir: parseSort(sort).dir, companyId,
  });

  // SOUND-787 — a parent's tab rolls up its child companies' credentials (the
  // API expands `companyId` server-side, as it does for MIDs). When any row is
  // owned by another company, each row names its owner — a parent can read a
  // child's credential but cannot change it, so whose it is matters.
  const aggregated = isAggregatedCredentialView(credentials, companyId);

  const columns = useMemo(
    () => [
      aggregated && {
        key: "owner",
        width: "16%",
        header: "Owner",
        // A real sort: the list route orders by the joined owner name.
        sortKey: "companyName",
        exportValue: (c) => c.companyName,
        cell: (c) => <div className="truncate font-medium">{credentialOwnerLabel(c)}</div>,
      },
      {
        key: "label",
        width: aggregated ? "22%" : "30%",
        header: "Credential",
        sortKey: "label",
        cell: (c) => <div className="truncate font-medium">{c.label || "—"}</div>,
      },
      { key: "provider", width: aggregated ? "12%" : "16%", header: "Provider", sortKey: "provider",
        cell: (c) => <span className="capitalize">{c.provider || "—"}</span> },
      { key: "kind", width: "12%", header: "Type", sortKey: "credentialType",
        cell: (c) => <RecordStatusPill status={c.credentialType} toneFor={kindTone} /> },
      { key: "storage", width: aggregated ? "14%" : "18%", header: "Secret storage",
        cell: (c) => hasSecretStorage(c)
          ? <span className="text-xs text-muted-foreground">{Object.keys(c.vault.fields).join(", ")}</span>
          : <span className="badge badge-warning">None</span> },
      { key: "lastAccess", width: "12%", header: "Last used",
        cell: (c) => c.lastAccess?.at
          ? <span className="text-xs text-muted-foreground"><RelativeTime value={c.lastAccess.at} /></span>
          : <span className="text-xs text-muted-foreground">Never</span> },
      { key: "updatedAt", width: "12%", header: "Updated", sortKey: "updatedAt",
        cell: (c) => <RelativeTime value={c.updatedAt} /> },
    ].filter(Boolean),
    [aggregated]
  );

  return (
    <div className="space-y-4">
      {(summary.noSecretStorage ?? 0) > 0 && (
        <div className="rounded-lg border border-warning bg-warning/10 px-4 py-2 text-sm">
          {summary.noSecretStorage} of this client's credentials have nowhere to store a secret.
        </div>
      )}
      <ListSearchBar
        value={q}
        onChange={(v) => { setQ(v); setPage(1); }}
        placeholder="Search by label, provider or username"
      >
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add credential
          </button>
        )}
      </ListSearchBar>
      <RecordTable
        columns={columns}
        rows={credentials}
        rowKey={(c) => c.credentialId || c._id}
        // A row with no credentialId is a credential the A1 migration has not
        // reached — against an unmigrated database that is EVERY row, and the
        // old code navigated to /credentials/undefined. Refusing the click is
        // the honest response: there is no detail page for a record that has no
        // stable id yet.
        onRowClick={(c) => c.credentialId && navigate(`/credentials/${c.credentialId}`)}
        rowClassName={(c) => (c.credentialId ? "" : "opacity-60")}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={Boolean(q)}
        sort={sort}
        onSortChange={(next) => { setSort(next); setPage(1); }}
        empty={{
          // EmptyState takes `icon` as a COMPONENT REFERENCE and renders
          // <Icon />; passing an element makes React receive an object as a
          // type. The keys are heading/description/errorText, not title.
          icon: KeyRound,
          heading: "No credentials for this client",
          description: "Stored API and portal credentials will appear here.",
          errorText: "Failed to load credentials.",
        }}
        noMatch={{ heading: "No matching credentials" }}
      />
      <ListPagination
        page={pagination.page}
        limit={pagination.limit}
        total={pagination.total}
        totalPages={pagination.totalPages}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
      />

      {/* SOUND-709 surface 2 — the company is known, so it is never asked for.
          It is passed as fixedCompanyId and shown as context instead. */}
      {showCreate && (
        <CredentialCreateModal
          companyId={companyId}
          contextLabel="This credential will be owned by this client."
          onClose={() => setShowCreate(false)}
          onCreated={refetch}
        />
      )}
    </div>
  );
}
